import { z } from 'zod';

/**
 * Single source of truth for tenant config (business.json) and booking input.
 * Used by the browser app, the Vite tenants plugin and the CLI scripts.
 */

export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const ID_RE = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
const HHMM = '([01]\\d|2[0-3]):[0-5]\\d';
const HOURS_RE = new RegExp(`^${HHMM}-${HHMM}$`);
const COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export const LOCALES = ['de', 'en'] as const;
export const localeSchema = z.enum(LOCALES);
export type Locale = z.infer<typeof localeSchema>;

export const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

const hoursSchema = z
  .string()
  .refine((v) => v === 'closed' || HOURS_RE.test(v), 'Expected "HH:MM-HH:MM" or "closed"')
  .refine((v) => {
    if (v === 'closed') return true;
    const [open, close] = v.split('-');
    return open! < close!;
  }, 'Opening time must be before closing time');

const localizedText = {
  name_de: z.string().min(1).optional(),
  name_en: z.string().min(1).optional(),
  description: z.string().optional(),
  description_de: z.string().optional(),
  description_en: z.string().optional(),
};

export const serviceSchema = z.object({
  id: z.string().regex(ID_RE),
  name: z.string().min(1).max(80),
  ...localizedText,
  duration_min: z.number().int().min(5).max(480),
  price: z.number().min(0).max(10_000),
  color: z.string().regex(COLOR_RE).optional(),
});

export const barberSchema = z.object({
  id: z.string().regex(ID_RE),
  name: z.string().min(1).max(80),
  bio: z.string().max(300).optional(),
  bio_de: z.string().max(300).optional(),
  bio_en: z.string().max(300).optional(),
  services: z.array(z.string()).min(1),
  image: z.string().optional(),
});

export const businessSchema = z
  .object({
    slug: z.string().regex(SLUG_RE).max(48),
    name: z.string().min(1).max(100),
    short_name: z.string().min(1).max(30),
    locale: localeSchema,
    timezone: z.string().default('Europe/Berlin'),
    currency: z.string().length(3).default('EUR'),
    logo: z.string().optional(),
    theme: z
      .object({
        primary: z.string().regex(COLOR_RE),
        accent: z.string().regex(COLOR_RE).optional(),
      })
      .default({ primary: '#1f2937' }),
    contact: z.object({
      phone: z.string().min(3),
      email: z.email(),
      website: z.url().optional(),
    }),
    location: z.object({
      address: z.string().min(3),
      city: z.string().min(1),
      postcode: z.string().min(3),
      country: z.string().length(2),
      coordinates: z.object({ lat: z.number().min(-90).max(90), lon: z.number().min(-180).max(180) }).optional(),
    }),
    business_hours: z.object({
      mon: hoursSchema,
      tue: hoursSchema,
      wed: hoursSchema,
      thu: hoursSchema,
      fri: hoursSchema,
      sat: hoursSchema,
      sun: hoursSchema,
    }),
    closed_dates: z.array(z.iso.date()).default([]),
    services: z.array(serviceSchema).min(1),
    barbers: z.array(barberSchema).min(1),
    rules: z.object({
      min_advance_booking_hours: z.number().min(0).max(168),
      max_advance_booking_days: z.number().int().min(1).max(365),
      cancellation_hours: z.number().min(0).max(168),
      max_clients_per_day: z.number().int().min(1).max(1000),
      slot_step_min: z.number().int().min(5).max(60).default(15),
    }),
    /** Anbieterkennzeichnung (§ 5 DDG) — shown on the shop's Impressum page. */
    legal: z.object({
      /** Inhaber:in or vertretungsberechtigte Person. */
      owner: z.string().min(2).max(100),
      /** Firmenname incl. Rechtsform, if different from `name` (e.g. "Deluxe Cut GmbH"). */
      company: z.string().max(150).optional(),
      /** e.g. "Amtsgericht Bochum, HRB 12345" */
      register: z.string().max(150).optional(),
      /** USt-IdNr. (§ 27a UStG), e.g. "DE123456789" */
      vat_id: z.string().regex(/^DE\d{9}$/).optional(),
    }),
    notifications: z
      .object({
        /** Where new-booking/cancellation alerts go; defaults to contact.email. */
        staff_email: z.email().optional(),
        notify_staff: z.boolean().default(true),
      })
      .default({ notify_staff: true }),
  })
  .superRefine((b, ctx) => {
    const serviceIds = new Set<string>();
    b.services.forEach((s, i) => {
      if (serviceIds.has(s.id)) ctx.addIssue({ code: 'custom', path: ['services', i, 'id'], message: `Duplicate service id "${s.id}"` });
      serviceIds.add(s.id);
    });
    const barberIds = new Set<string>();
    b.barbers.forEach((barber, i) => {
      if (barberIds.has(barber.id)) ctx.addIssue({ code: 'custom', path: ['barbers', i, 'id'], message: `Duplicate barber id "${barber.id}"` });
      barberIds.add(barber.id);
      barber.services.forEach((sid, j) => {
        if (!serviceIds.has(sid)) {
          ctx.addIssue({ code: 'custom', path: ['barbers', i, 'services', j], message: `Unknown service "${sid}"` });
        }
      });
    });
    b.services.forEach((s, i) => {
      if (!b.barbers.some((barber) => barber.services.includes(s.id))) {
        ctx.addIssue({ code: 'custom', path: ['services', i], message: `No barber offers service "${s.id}"` });
      }
    });
  });

export type Business = z.infer<typeof businessSchema>;
export type Service = z.infer<typeof serviceSchema>;
export type Barber = z.infer<typeof barberSchema>;

// --- Bookings ---------------------------------------------------------------

export const BOOKING_STATUSES = ['pending', 'confirmed', 'completed', 'cancelled', 'no_show'] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];
/** Statuses that occupy a barber's time. */
export const ACTIVE_STATUSES: readonly BookingStatus[] = ['pending', 'confirmed'];

/** Strip control chars and collapse whitespace; output is still rendered via textContent only. */
export function cleanText(v: string): string {
  // eslint-disable-next-line no-control-regex -- stripping control chars is the point
  return v.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * German-market E.164 normalisation, mirrored by public.normalize_phone() in SQL:
 * "0151 234 5678" -> "+491512345678", "0041 44…" -> "+4144…".
 */
export function normalizePhone(v: string): string {
  const s = v.replace(/[\s()/.-]/g, '');
  if (s.startsWith('00')) return `+${s.slice(2)}`;
  if (s.startsWith('0')) return `+49${s.slice(1)}`;
  return s;
}

export const contactSchema = z.object({
  client_name: z.string().transform(cleanText).pipe(z.string().min(2, 'name_short').max(100, 'name_long')),
  client_phone: z
    .string()
    .transform(normalizePhone)
    .pipe(z.string().regex(/^\+\d{7,15}$/, 'phone_invalid')),
  client_email: z
    .string()
    .trim()
    .transform((v) => (v === '' ? undefined : v.toLowerCase()))
    .pipe(z.email('email_invalid').max(254).optional()),
});
export type ContactInput = z.input<typeof contactSchema>;
export type Contact = z.output<typeof contactSchema>;

export const newBookingSchema = contactSchema.extend({
  service_id: z.string().regex(ID_RE),
  barber_id: z.string().regex(ID_RE),
  starts_at: z.iso.datetime(),
  /** Language for confirmation and reminder e-mails. */
  locale: localeSchema.default('de'),
});
export type NewBooking = z.output<typeof newBookingSchema>;

export interface Booking extends NewBooking {
  id: string;
  tenant_slug: string;
  duration_min: number;
  ends_at: string;
  price: number;
  status: BookingStatus;
  /** Secret that lets the client cancel without an account. */
  cancel_token: string;
  created_at: string;
}

/** Returns human-readable issues, e.g. for the CLI validator. */
export function formatIssues(error: z.ZodError): string[] {
  return error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`);
}
